import { useMemo } from 'react';
import { getIsoWeekNumber } from '../utils/isoDates.js';
import {
  applyStateFilter,
  getFilteredTimeslots,
  getPlanningItemPromoterIds,
  getPlanningItemStates
} from '../utils/giPlanningStateUtils.js';
import { processClientGrouping } from '../utils/giPlanningGrouping.js';

export default function useGIPlanningDerivedData({
  planningData,
  selectedStates,
  selectedWeek,
  selectedCampaign,
  selectedClient,
  selectedCompany,
  expandedClients,
  displayMode
}) {
  // Lookup maps
  const campaignsMap = useMemo(() => {
    const map = new Map();
    (planningData.campaigns || []).forEach(campaign => {
      map.set(campaign.Id, campaign);
    });
    return map;
  }, [planningData.campaigns]);

  const promotersMap = useMemo(() => {
    const map = new Map();
    (planningData.promoters || []).forEach(promoter => {
      map.set(promoter.Id, promoter);
    });
    return map;
  }, [planningData.promoters]);

  const interventionPointsMap = useMemo(() => {
    const map = new Map();
    (planningData.interventionPoints || []).forEach(point => {
      map.set(point.Id, point);
    });
    return map;
  }, [planningData.interventionPoints]);

  // Current week/date
  const currentWeek = useMemo(() => {
    const today = new Date();
    return getIsoWeekNumber(today).fullWeekString;
  }, []);

  const currentDate = useMemo(() => {
    return new Date().toISOString().split('T')[0];
  }, []);

  // Unique weeks
  const uniqueWeeks = useMemo(() => {
    if (!planningData.planning?.length) return [];
    const weeks = new Set();
    planningData.planning.forEach(item => {
      const date = new Date(item.Date);
      const weekInfo = getIsoWeekNumber(date);
      weeks.add(weekInfo.fullWeekString);
    });
    return Array.from(weeks).sort();
  }, [planningData.planning]);

  // Unique dates (optionally filtered by selectedWeek and selectedStates)
  const uniqueDates = useMemo(() => {
    if (!planningData.planning?.length) return [];

    let filteredPlanning = planningData.planning;
    if (selectedWeek) {
      filteredPlanning = filteredPlanning.filter(item => {
        const date = new Date(item.Date);
        const weekInfo = getIsoWeekNumber(date);
        return weekInfo.fullWeekString === selectedWeek;
      });
    }

    if (selectedStates?.length > 0) {
      filteredPlanning = applyStateFilter(filteredPlanning, selectedStates);
    }

    if (!filteredPlanning.length) return [];

    const datesWithData = new Set();
    filteredPlanning.forEach(item => datesWithData.add(item.Date));
    const sortedDatesWithData = Array.from(datesWithData).sort();
    if (sortedDatesWithData.length <= 1) return sortedDatesWithData;

    const startDate = new Date(sortedDatesWithData[0]);
    const endDate = new Date(sortedDatesWithData[sortedDatesWithData.length - 1]);
    const allDatesSet = new Set();
    const current = new Date(startDate);
    while (current <= endDate) {
      allDatesSet.add(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }
    return Array.from(allDatesSet).sort();
  }, [planningData.planning, selectedWeek, selectedStates]);

  // Weekly (group by campaign or client)
  const weeklyData = useMemo(() => {
    if (!planningData.planning?.length) return [];

    const filtered = selectedStates?.length
      ? applyStateFilter(planningData.planning, selectedStates)
      : planningData.planning;

    const shouldGroupByClient = selectedCompany && !selectedClient;
    if (shouldGroupByClient) {
      return processClientGrouping({
        filteredPlanning: filtered,
        timeUnit: 'weeks',
        campaignsMap,
        expandedClients,
        selectedStates
      });
    }

    const campaignMap = new Map();

    filtered.forEach(item => {
      const campaign = campaignsMap.get(item.CampaignId);
      if (!campaign) return;

      const campaignKey = campaign.Name;
      const date = new Date(item.Date);
      const weekInfo = getIsoWeekNumber(date);
      const weekKey = weekInfo.fullWeekString;
      const promoterIds = getPlanningItemPromoterIds(item, selectedStates);
      const states = getPlanningItemStates(item, selectedStates);
      const isActive = campaign.IsActive;

      if (!campaignMap.has(campaignKey)) {
        const clientName = item.Client || campaign.ClientName || '';
        campaignMap.set(campaignKey, {
          campaign: campaignKey,
          isActive: isActive,
          client: clientName,
          company: item.Company || '',
          companyId: item.CompanyId || null,
          entity: item.Entity || '',
          entityId: item.EntityId || null,
          weeks: new Map()
        });
      }

      const campaignData = campaignMap.get(campaignKey);
      if (!campaignData.weeks.has(weekKey)) {
        campaignData.weeks.set(weekKey, {
          promoters: new Set(),
          states: new Map()
        });
      }

      const weekData = campaignData.weeks.get(weekKey);
      promoterIds.forEach((promoterId) => weekData.promoters.add(promoterId));
      states.forEach((state) => {
        weekData.states.set(state, (weekData.states.get(state) || 0) + 1);
      });
    });

    return Array.from(campaignMap.values()).map(campaign => {
      const weeks = Array.from(campaign.weeks.keys()).sort();
      return {
        campaign: campaign.campaign,
        client: campaign.client,
        company: campaign.company,
        companyId: campaign.companyId,
        entity: campaign.entity,
        entityId: campaign.entityId,
        isActive: campaign.isActive,
        firstWeek: weeks[0],
        data: Object.fromEntries(
          Array.from(campaign.weeks.entries()).map(([week, weekData]) => [
            week,
            {
              count: weekData.promoters.size,
              states: Object.fromEntries(weekData.states)
            }
          ])
        )
      };
    }).sort((a, b) => a.firstWeek.localeCompare(b.firstWeek));
  }, [planningData.planning, campaignsMap, selectedClient, selectedCompany, selectedStates, expandedClients]);

  // Weekly Intervention Points
  const weeklyInterventionPointsData = useMemo(() => {
    if (!planningData.planning?.length || displayMode !== 'interventionPoints') return [];

    const filtered = selectedStates?.length
      ? applyStateFilter(planningData.planning, selectedStates)
      : planningData.planning;

    const interventionPointMap = new Map();

    filtered.forEach(item => {
      const interventionPoint = interventionPointsMap.get(item.MissionInterventionPointId);
      if (!interventionPoint) return;

      const interventionPointKey = interventionPoint.Name;
      const date = new Date(item.Date);
      const weekInfo = getIsoWeekNumber(date);
      const weekKey = weekInfo.fullWeekString;
      const promoterIds = getPlanningItemPromoterIds(item, selectedStates);
      const states = getPlanningItemStates(item, selectedStates);

      if (!interventionPointMap.has(interventionPointKey)) {
        const clientName = selectedCompany ? (item.Client || '') : (selectedClient?.label || '');
        interventionPointMap.set(interventionPointKey, {
          interventionPoint: interventionPointKey,
          client: clientName,
          weeks: new Map()
        });
      }

      const interventionPointData = interventionPointMap.get(interventionPointKey);
      if (!interventionPointData.weeks.has(weekKey)) {
        interventionPointData.weeks.set(weekKey, {
          promoters: new Set(),
          states: new Map()
        });
      }

      const weekData = interventionPointData.weeks.get(weekKey);
      promoterIds.forEach((promoterId) => weekData.promoters.add(promoterId));
      states.forEach((state) => {
        weekData.states.set(state, (weekData.states.get(state) || 0) + 1);
      });
    });

    return Array.from(interventionPointMap.values()).map(interventionPoint => {
      const weeks = Array.from(interventionPoint.weeks.keys()).sort();
      return {
        campaign: interventionPoint.interventionPoint,
        client: interventionPoint.client,
        entity: interventionPoint.entity,
        entityId: interventionPoint.entityId,
        company: interventionPoint.company,
        companyId: interventionPoint.companyId,
        firstWeek: weeks[0],
        data: Object.fromEntries(
          Array.from(interventionPoint.weeks.entries()).map(([week, weekData]) => [
            week,
            {
              count: weekData.promoters.size,
              states: Object.fromEntries(weekData.states)
            }
          ])
        )
      };
    }).sort((a, b) => a.firstWeek.localeCompare(b.firstWeek));
  }, [planningData.planning, interventionPointsMap, selectedClient, selectedCompany, selectedStates, displayMode]);

  // Overview (daily) grouped by campaign or client
  const overviewData = useMemo(() => {
    if (!planningData.planning?.length) return [];

    let filteredPlanning = planningData.planning;
    if (selectedWeek) {
      filteredPlanning = filteredPlanning.filter(item => {
        const date = new Date(item.Date);
        const weekInfo = getIsoWeekNumber(date);
        return weekInfo.fullWeekString === selectedWeek;
      });
    }
    if (selectedStates?.length > 0) {
      filteredPlanning = applyStateFilter(filteredPlanning, selectedStates);
    }

    const shouldGroupByClient = selectedCompany && !selectedClient;
    if (shouldGroupByClient) {
      return processClientGrouping({
        filteredPlanning,
        timeUnit: 'dates',
        campaignsMap,
        expandedClients,
        selectedStates
      });
    }

    const campaignMap = new Map();
    filteredPlanning.forEach(item => {
      const campaign = campaignsMap.get(item.CampaignId);
      if (!campaign) return;

      const campaignKey = campaign.Name;
      const date = item.Date;
      const promoterIds = getPlanningItemPromoterIds(item, selectedStates);
      const states = getPlanningItemStates(item, selectedStates);
      const isActive = campaign.IsActive;

      if (!campaignMap.has(campaignKey)) {
        const clientName = item.Client || campaign.ClientName || '';
        campaignMap.set(campaignKey, {
          campaign: campaignKey,
          isActive: isActive,
          client: clientName,
          company: item.Company || '',
          companyId: item.CompanyId || null,
          entity: item.Entity || '',
          entityId: item.EntityId || null,
          dates: new Map()
        });
      }

      const campaignData = campaignMap.get(campaignKey);
      if (!campaignData.dates.has(date)) {
        campaignData.dates.set(date, {
          promoters: new Set(),
          states: new Map()
        });
      }

      const dateData = campaignData.dates.get(date);
      promoterIds.forEach((promoterId) => dateData.promoters.add(promoterId));
      states.forEach((state) => {
        dateData.states.set(state, (dateData.states.get(state) || 0) + 1);
      });
    });

    return Array.from(campaignMap.values()).map(campaign => {
      const dates = Array.from(campaign.dates.keys()).sort();
      return {
        campaign: campaign.campaign,
        isActive: campaign.isActive,
        client: campaign.client,
        company: campaign.company,
        companyId: campaign.companyId,
        entity: campaign.entity,
        entityId: campaign.entityId,
        firstDate: dates[0],
        data: Object.fromEntries(
          Array.from(campaign.dates.entries()).map(([date, dateData]) => [
            date,
            {
              count: dateData.promoters.size,
              states: Object.fromEntries(dateData.states)
            }
          ])
        )
      };
    }).sort((a, b) => a.firstDate.localeCompare(b.firstDate));
  }, [planningData.planning, selectedWeek, campaignsMap, selectedClient, selectedCompany, selectedStates, expandedClients]);

  // Overview daily for intervention points
  const overviewInterventionPointsData = useMemo(() => {
    if (!planningData.planning?.length || displayMode !== 'interventionPoints') return [];

    let filteredPlanning = planningData.planning;
    if (selectedWeek) {
      filteredPlanning = filteredPlanning.filter(item => {
        const date = new Date(item.Date);
        const weekInfo = getIsoWeekNumber(date);
        return weekInfo.fullWeekString === selectedWeek;
      });
    }
    if (selectedStates?.length > 0) {
      filteredPlanning = applyStateFilter(filteredPlanning, selectedStates);
    }

    const interventionPointMap = new Map();
    filteredPlanning.forEach(item => {
      const interventionPoint = interventionPointsMap.get(item.MissionInterventionPointId);
      if (!interventionPoint) return;

      const interventionPointKey = interventionPoint.Name;
      const date = item.Date;
      const promoterIds = getPlanningItemPromoterIds(item, selectedStates);
      const states = getPlanningItemStates(item, selectedStates);

      if (!interventionPointMap.has(interventionPointKey)) {
        const clientName = selectedCompany ? (item.Client || '') : (selectedClient?.label || '');
        interventionPointMap.set(interventionPointKey, {
          interventionPoint: interventionPointKey,
          client: clientName,
          dates: new Map()
        });
      }

      const interventionPointData = interventionPointMap.get(interventionPointKey);
      if (!interventionPointData.dates.has(date)) {
        interventionPointData.dates.set(date, {
          promoters: new Set(),
          states: new Map()
        });
      }

      const dateData = interventionPointData.dates.get(date);
      promoterIds.forEach((promoterId) => dateData.promoters.add(promoterId));
      states.forEach((state) => {
        dateData.states.set(state, (dateData.states.get(state) || 0) + 1);
      });
    });

    return Array.from(interventionPointMap.values()).map(interventionPoint => {
      const dates = Array.from(interventionPoint.dates.keys()).sort();
      return {
        campaign: interventionPoint.interventionPoint,
        client: interventionPoint.client,
        firstDate: dates[0],
        data: Object.fromEntries(
          Array.from(interventionPoint.dates.entries()).map(([date, dateData]) => [
            date,
            {
              count: dateData.promoters.size,
              states: Object.fromEntries(dateData.states)
            }
          ])
        )
      };
    }).sort((a, b) => a.firstDate.localeCompare(b.firstDate));
  }, [planningData.planning, selectedWeek, interventionPointsMap, selectedClient, selectedCompany, selectedStates, displayMode]);

  // Detailed data for selected campaign (promoters)
  const detailedData = useMemo(() => {
    if (!planningData.planning?.length || !selectedCampaign) return [];

    let filteredPlanning = planningData.planning;
    if (selectedWeek) {
      filteredPlanning = planningData.planning.filter(item => {
        const date = new Date(item.Date);
        const weekInfo = getIsoWeekNumber(date);
        return weekInfo.fullWeekString === selectedWeek;
      });
    }

    let campaignPlanning = filteredPlanning.filter(item => {
      const campaign = campaignsMap.get(item.CampaignId);
      return campaign && campaign.Name === selectedCampaign;
    });

    if (selectedStates?.length > 0) {
      campaignPlanning = applyStateFilter(campaignPlanning, selectedStates);
    }

    const promoterMap = new Map();

    campaignPlanning.forEach(item => {
      const date = item.Date;
      const timeslots = getFilteredTimeslots(item, selectedStates);

      timeslots.forEach((timeslot) => {
        const promoter = promotersMap.get(timeslot.PromoterId);
        if (!promoter) return;

        const promoterKey = promoter.Name;

        if (!promoterMap.has(promoterKey)) {
          promoterMap.set(promoterKey, {
            promoter: promoterKey,
            promoterId: promoter.Id,
            dates: new Set(),
            dateStates: new Map()
          });
        }

        const promoterData = promoterMap.get(promoterKey);
        promoterData.dates.add(date);

        if (!promoterData.dateStates.has(date)) {
          promoterData.dateStates.set(date, new Map());
        }

        const state = timeslot.State || item.State || 'Autre';
        const dateStateMap = promoterData.dateStates.get(date);
        dateStateMap.set(state, (dateStateMap.get(state) || 0) + 1);
      });
    });

    return Array.from(promoterMap.values()).map(promoter => {
      const sortedDates = Array.from(promoter.dates).sort();
      return {
        ...promoter,
        dates: sortedDates,
        firstDate: sortedDates[0]
      };
    }).sort((a, b) => a.firstDate.localeCompare(b.firstDate));
  }, [planningData.planning, selectedCampaign, selectedWeek, campaignsMap, promotersMap, selectedStates]);

  // Detailed data for selected campaign (intervention points)
  const interventionPointsData = useMemo(() => {
    if (!planningData.planning?.length || !selectedCampaign) return [];

    let filteredPlanning = planningData.planning;
    if (selectedWeek) {
      filteredPlanning = planningData.planning.filter(item => {
        const date = new Date(item.Date);
        const weekInfo = getIsoWeekNumber(date);
        return weekInfo.fullWeekString === selectedWeek;
      });
    }

    let campaignPlanning = filteredPlanning.filter(item => {
      const campaign = campaignsMap.get(item.CampaignId);
      return campaign && campaign.Name === selectedCampaign;
    });

    if (selectedStates?.length > 0) {
      campaignPlanning = applyStateFilter(campaignPlanning, selectedStates);
    }

    const interventionPointMap = new Map();

    campaignPlanning.forEach(item => {
      const interventionPoint = interventionPointsMap.get(item.MissionInterventionPointId);
      if (!interventionPoint) return;

      const interventionPointKey = interventionPoint.Name;
      const date = item.Date;

      if (!interventionPointMap.has(interventionPointKey)) {
        interventionPointMap.set(interventionPointKey, {
          interventionPoint: interventionPointKey,
          dates: new Set(),
          dateStates: new Map()
        });
      }

      const interventionPointData = interventionPointMap.get(interventionPointKey);
      interventionPointData.dates.add(date);

      if (!interventionPointData.dateStates.has(date)) {
        interventionPointData.dateStates.set(date, new Map());
      }
      const dateStateMap = interventionPointData.dateStates.get(date);
      getFilteredTimeslots(item, selectedStates).forEach((timeslot) => {
        const state = timeslot.State || item.State || 'Autre';
        dateStateMap.set(state, (dateStateMap.get(state) || 0) + 1);
      });
    });

    return Array.from(interventionPointMap.values()).map(point => {
      const sortedDates = Array.from(point.dates).sort();
      return {
        ...point,
        dates: sortedDates,
        firstDate: sortedDates[0]
      };
    }).sort((a, b) => a.firstDate.localeCompare(b.firstDate));
  }, [planningData.planning, selectedCampaign, selectedWeek, campaignsMap, interventionPointsMap, selectedStates]);

  // Detailed dates range for selected campaign
  const detailedDates = useMemo(() => {
    if (!planningData.planning?.length || !selectedCampaign) return [];

    let filteredPlanning = planningData.planning;
    if (selectedWeek) {
      filteredPlanning = planningData.planning.filter(item => {
        const date = new Date(item.Date);
        const weekInfo = getIsoWeekNumber(date);
        return weekInfo.fullWeekString === selectedWeek;
      });
    }

    let campaignPlanning = filteredPlanning.filter(item => {
      const campaign = campaignsMap.get(item.CampaignId);
      return campaign && campaign.Name === selectedCampaign;
    });

    if (selectedStates?.length > 0) {
      campaignPlanning = applyStateFilter(campaignPlanning, selectedStates);
    }

    if (!campaignPlanning.length) return [];

    const datesWithData = new Set();
    campaignPlanning.forEach(item => datesWithData.add(item.Date));
    const sortedDatesWithData = Array.from(datesWithData).sort();
    if (sortedDatesWithData.length <= 1) return sortedDatesWithData;

    const startDate = new Date(sortedDatesWithData[0]);
    const endDate = new Date(sortedDatesWithData[sortedDatesWithData.length - 1]);
    const allDatesSet = new Set();
    const current = new Date(startDate);
    while (current <= endDate) {
      allDatesSet.add(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }
    return Array.from(allDatesSet).sort();
  }, [planningData.planning, selectedCampaign, selectedWeek, campaignsMap, selectedStates]);

  // Columns
  const weeklyColumns = useMemo(() => {
    const weeklyTotals = {};
    const currentData = displayMode === 'campaigns' ? weeklyData : weeklyInterventionPointsData;

    currentData.forEach(row => {
      if (!row.isClientGroup) {
        Object.entries(row.data || {}).forEach(([week, weekData]) => {
          if (!weeklyTotals[week]) weeklyTotals[week] = 0;
          weeklyTotals[week] += weekData.count || 0;
        });
      }
    });

    return uniqueWeeks.map(week => ({
      key: week,
      label: week,
      sublabel: weeklyTotals[week] ? `#${weeklyTotals[week]}` : null,
      isCurrent: week === currentWeek
    }));
  }, [uniqueWeeks, currentWeek, weeklyData, weeklyInterventionPointsData, displayMode]);

  const dailyColumns = useMemo(() => {
    const dailyTotals = {};
    const currentData = displayMode === 'campaigns' ? overviewData : overviewInterventionPointsData;

    currentData.forEach(row => {
      if (!row.isClientGroup) {
        Object.entries(row.data || {}).forEach(([date, dateData]) => {
          if (!dailyTotals[date]) dailyTotals[date] = 0;
          dailyTotals[date] += dateData.count || 0;
        });
      }
    });

    return uniqueDates.map(date => ({
      key: date,
      label: new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
      sublabel: dailyTotals[date] ? `#${dailyTotals[date]}` : null,
      isCurrent: date === currentDate
    }));
  }, [uniqueDates, currentDate, overviewData, overviewInterventionPointsData, displayMode]);

  // Performance guard for intervention points
  const tooManyInterventionPoints = useMemo(() => {
    return (planningData.interventionPoints || []).length > 500;
  }, [planningData.interventionPoints]);

  return {
    // datasets
    weeklyData,
    overviewData,
    weeklyInterventionPointsData,
    overviewInterventionPointsData,
    detailedData,
    interventionPointsData,
    detailedDates,
    // columns & date helpers
    weeklyColumns,
    dailyColumns,
    currentWeek,
    currentDate,
    uniqueWeeks,
    uniqueDates,
    // maps (useful to callers)
    campaignsMap,
    promotersMap,
    interventionPointsMap,
    // guards
    tooManyInterventionPoints
  };
}


